import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Checkbox,
  Button,
  Stack,
} from "@mui/material";
import api from "../../api/axios";

// Helper function to get primary media URL
const getPrimaryMediaUrl = (product) => {
  // ✅ For Similar Products API
  if (product?.imageUrl) {
    return product.imageUrl;
  }

  // ✅ For Full Product API
  if (product?.images?.length > 0 && product.images[0]?.url) {
    return product.images[0].thumbnailUrl;
  }

  if (product?.videos?.length > 0 && product.videos[0]?.thumbnailUrl) {
    return product.videos[0].thumbnailUrl;
  }

  return "/placeholder-product.png";
};

const FrequentlyBoughtTogether = ({ product }) => {
  const navigate = useNavigate();

  const [items, setItems] = React.useState([]);
  const [selected, setSelected] = React.useState({});
  const [quantities, setQuantities] = React.useState({});

  React.useEffect(() => {
    if (!product?._id) return;

    api
      .get(`/products/${product._id}/frequently-bought-together`)
      .then((res) => {
        const list = [product, ...(res.data.products || [])];
        setItems(list);

        const initial = {};
        list.forEach((p) => (initial[p._id] = true));
        setSelected(initial);
      });
  }, [product]);

  const toggle = (id) => {
    setSelected((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const selectedItems = items.filter((p) => selected[p._id]);

  const totalPrice = selectedItems.reduce(
    (sum, p) => sum + Number(p.price || 0),
    0
  );

  const productBoxSx = items.length <= 1
    ? {
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: 1,
        flex: "0 0 auto",
        minWidth: 0,
        overflowX: "auto",
      }
    : {
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: 1,
        flex: 1,
        minWidth: 0,
        overflowX: { xs: "auto", md: "visible" },
      };

  if (!items.length) return null;

  return (
    <Paper
      sx={{
        mt: 5,
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography fontWeight={700} fontSize={18} mb={3}>
        Frequently Bought Together
      </Typography>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={4}
        alignItems="stretch"
      >
        {/* PRODUCTS (Horizontal Scroll on Mobile) */}
        <Box sx={productBoxSx}>
          {items.map((item, index) => (
            <React.Fragment key={item._id}>
              {index > 0 && (
                <Typography sx={{ fontSize: 20, color: "#bbb" }}>
                  +
                </Typography>
              )}

              <Box
                sx={{
                  width: { xs: 70, sm: 120 },
                  textAlign: "center",
                }}
              >
                <Checkbox
                  size="small"
                  checked={!!selected[item._id]}
                  onChange={() => toggle(item._id)}
                  sx={{ p: 0 }}
                />

                <Box
                  sx={{
                    height: { xs: 60, sm: 100 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #eee",
                    borderRadius: 1,
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(`/product/${item._id}`)}
                >
                  <img
                    src={item.imageUrl || getPrimaryMediaUrl(item)}
                    alt={item.title}
                    style={{
                      maxHeight: "100%",
                      maxWidth: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>

                <Typography
                  sx={{
                    fontSize: 11,
                    mt: 0.5,
                    height: 30,
                    overflow: "hidden",
                  }}
                >
                  {item.title}
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    color: "#B12704",
                  }}
                >
                  ₹{Number(item.price || 0).toLocaleString("en-IN")}
                </Typography>
              </Box>
            </React.Fragment>
          ))}
        </Box>

        {/* TOTAL SECTION */}
        <Box
          sx={{
            flex: "0 0 auto",
            width: { xs: "100%", md: 240 },
            maxWidth: { xs: "100%", md: 240 },
            boxSizing: "border-box",
            borderLeft: { xs: "none", md: "1px solid #eee" },
            borderTop: { xs: "1px solid #eee", md: "none" },
            pl: { xs: 0, md: 3 },
            pt: { xs: 2, md: 0 },
          }}
        >
          <Typography fontSize={14} mb={0.5}>
            Total price:
          </Typography>

          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 700,
              color: "#B12704",
              mb: 0.5,
            }}
          >
            ₹{Number(totalPrice || 0).toLocaleString("en-IN")}
          </Typography>

          <Typography fontSize={13} color="#555" mb={2}>
            for {selectedItems.length} item
            {selectedItems.length > 1 ? "s" : ""}
          </Typography>

          <Button
            fullWidth
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1,
              backgroundColor: "#FFD814",
              color: "#111",
              "&:hover": {
                backgroundColor: "#F7CA00",
              },
            }}
            onClick={() => {
              const selectedItems = items.filter(
                (p) => selected[p._id]
              );

              console.log("Add to cart:", selectedItems);
            }}
          >
            Add {selectedItems.length} item
            {selectedItems.length > 1 ? "s" : ""} to Cart
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default FrequentlyBoughtTogether;